import { useMemo, useState } from 'react';
import { as2CertificatesApi } from '../api/as2Certificates';
import { ApiClientError } from '../api/http';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { CopyButton } from '../components/CopyButton';
import { PageHeader } from '../components/FeatureStatus';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { useAsyncData } from '../hooks/useAsyncData';
import { useIsFlowAdmin } from '../hooks/usePermission';
import type {
  As2CertificateCreateRequest,
  As2CertificateHealthStatus,
  As2CertificateListQuery,
  As2CertificateType,
} from '../types/api';

// NOTE: Certificates only support create + deactivate (never update or hard-delete) on the
// backend, so this page is intentionally bespoke rather than built on ConfigResourcePage —
// that component requires `update`/`remove` handlers and a fixed name/key/status/updated
// column layout that doesn't have room for expiry health, and doesn't fit this resource's
// create-then-deactivate lifecycle.

const CERTIFICATE_TYPES: As2CertificateType[] = ['LOCAL_SIGNING', 'PARTNER_SIGNING', 'PARTNER_ENCRYPTION'];
const HEALTH_STATUSES: As2CertificateHealthStatus[] = ['VALID', 'EXPIRING_SOON', 'CRITICAL', 'EXPIRED'];
const PAGE_SIZE = 20;

function defaultCreateForm(): As2CertificateCreateRequest {
  return {
    alias: '',
    certificateType: 'PARTNER_SIGNING',
    partnerCode: '',
    certificatePem: '',
    privateKeyReference: '',
  };
}

function registerCertificateDisabledReason(form: As2CertificateCreateRequest, isSubmitting: boolean): string | null {
  if (isSubmitting) return 'A certificate registration is already in progress.';
  if (!form.alias.trim()) return 'Alias is required.';
  if (!form.certificateType) return 'Certificate type is required.';
  if (!form.certificatePem.trim()) return 'Certificate PEM is required.';
  if (!form.certificatePem.includes('BEGIN CERTIFICATE')) return 'Paste a PEM certificate that starts with BEGIN CERTIFICATE.';
  if (form.certificateType === 'LOCAL_SIGNING' && !form.privateKeyReference?.trim()) return 'Local signing certificates require a private key reference.';
  return null;
}

export function As2CertificatesPage() {
  const canManage = useIsFlowAdmin();
  const [filters, setFilters] = useState<As2CertificateListQuery>({
    page: 0,
    size: PAGE_SIZE,
    certificateType: '',
    partnerCode: '',
    active: undefined,
  });
  const [form, setForm] = useState<As2CertificateCreateRequest>(defaultCreateForm());
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);

  const { data, isLoading, error, refresh } = useAsyncData(
    () => as2CertificatesApi.list(filters),
    [JSON.stringify(filters)],
    { debounceMs: 250 },
  );
  const { data: health, refresh: refreshHealth } = useAsyncData(() => as2CertificatesApi.health(), []);

  const healthById = useMemo(() => {
    const map = new Map<string, As2CertificateHealthStatus>();
    if (health) {
      for (const status of HEALTH_STATUSES) {
        for (const entry of health.certificates[status] ?? []) {
          map.set(entry.id, status);
        }
      }
    }
    return map;
  }, [health]);

  const certificates = data?.data ?? [];
  const registerDisabledReason = registerCertificateDisabledReason(form, isSubmitting);
  const urgentHealthCount = (health?.certificates.CRITICAL?.length ?? 0) + (health?.certificates.EXPIRED?.length ?? 0);

  function patchFilters(patch: Partial<As2CertificateListQuery>) {
    setFilters((current) => ({ ...current, ...patch, page: 0 }));
  }

  async function refreshAll() {
    await Promise.all([refresh(), refreshHealth()]);
  }

  async function submitForm(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);

    if (!form.alias.trim() || !form.certificateType || !form.certificatePem.trim()) {
      setSubmitError('Alias, certificate type, and certificate PEM are required.');
      return;
    }

    setIsSubmitting(true);
    try {
      await as2CertificatesApi.create({
        alias: form.alias.trim(),
        certificateType: form.certificateType,
        partnerCode: form.partnerCode?.trim() || undefined,
        certificatePem: form.certificatePem.trim(),
        privateKeyReference: form.privateKeyReference?.trim() || undefined,
      });
      setForm(defaultCreateForm());
      await refreshAll();
    } catch (caught) {
      setSubmitError(formatCertificateError(caught));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function deactivate(id: string) {
    try {
      await as2CertificatesApi.deactivate(id);
      await refreshAll();
    } catch (caught) {
      throw new Error(formatCertificateError(caught));
    }
  }

  return (
    <div className="page-grid two-columns">
      <PageHeader
        eyebrow="AS2 Gateway"
        title="AS2 certificates"
        purpose="Manage local signing and partner certificates used by the AS2 gateway, and watch expiry health."
        status="ADMIN_ONLY"
        connected
        next={canManage
          ? 'Registering or deactivating a certificate requires flow-admin. Read access is available to any user with flow access.'
          : 'Read-only: registering or deactivating a certificate requires the flow-admin role.'}
      />

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Certificate expiry health</h2>
            <p>Grouped counts from the AS2 gateway's certificate health check.</p>
          </div>
          <div className="panel-actions">
            <button type="button" onClick={() => void refreshAll()}>
              Refresh
            </button>
          </div>
        </div>
        <div className="operations-card-grid">
          {HEALTH_STATUSES.map((status) => (
            <article className="operations-card" key={status}>
              <span className="operations-card-label">{formatHealthLabel(status)}</span>
              <strong>{health?.certificates[status]?.length ?? 0}</strong>
              <StatusBadge value={status} />
            </article>
          ))}
        </div>
        {urgentHealthCount > 0 ? (
          <div className="recovery-callout certificate-health-callout" role="status">
            <div>
              <span className="operations-card-label">Action needed</span>
              <strong>{urgentHealthCount}</strong>
              <p>Expired or critical AS2 certificates can break signing, encryption, trust validation, or MDN handling. Replace them before partner traffic is affected.</p>
            </div>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Certificates</h2>
        </div>
        <div className="toolbar toolbar-grid">
          <select
            aria-label="Filter certificate type"
            value={filters.certificateType ?? ''}
            onChange={(event) => patchFilters({ certificateType: event.target.value as As2CertificateType | '' })}
          >
            <option value="">All certificate types</option>
            {CERTIFICATE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
          <input
            aria-label="Filter partner code"
            placeholder="Partner code"
            value={filters.partnerCode ?? ''}
            onChange={(event) => patchFilters({ partnerCode: event.target.value })}
          />
          <select
            aria-label="Active status"
            value={filters.active === undefined ? '' : String(filters.active)}
            onChange={(event) => patchFilters({ active: event.target.value === '' ? undefined : event.target.value === 'true' })}
          >
            <option value="">All statuses</option>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </select>
          <button
            type="button"
            className="ghost-button"
            onClick={() => setFilters({ page: 0, size: PAGE_SIZE, certificateType: '', partnerCode: '', active: undefined })}
          >
            Reset filters
          </button>
        </div>
        {isLoading ? <LoadingState label="Loading AS2 certificates..." /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && !error && certificates.length === 0 ? <EmptyState label="No AS2 certificates matched the current filters." /> : null}
        {!isLoading && !error && certificates.length > 0 ? (
          <>
            <div className="table-scroll" tabIndex={0} aria-label="AS2 certificates">
              <table className="data-table data-table-cozy as2-certificates-table">
                <thead>
                  <tr>
                    <th>Alias</th>
                    <th>Type</th>
                    <th>Partner</th>
                    <th>Fingerprint</th>
                    <th>Valid until</th>
                    <th>Health</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {certificates.map((certificate) => (
                    <tr key={certificate.id}>
                      <td data-label="Alias">{certificate.alias}</td>
                      <td data-label="Type">{certificate.certificateType}</td>
                      <td data-label="Partner">{certificate.partnerCode ?? 'n/a'}</td>
                      <td data-label="Fingerprint">
                        <span className="inline-copy">
                          <span className="mono" title={certificate.fingerprintSha256 ?? undefined}>
                            {shortFingerprint(certificate.fingerprintSha256)}
                          </span>
                          <CopyButton value={certificate.fingerprintSha256} label="Copy fingerprint" />
                        </span>
                      </td>
                      <td data-label="Valid until">{certificate.validUntil ? new Date(certificate.validUntil).toLocaleDateString('en-GB') : 'n/a'}</td>
                      <td data-label="Health">
                        {healthById.has(certificate.id) ? (
                          <StatusBadge value={healthById.get(certificate.id) as string} />
                        ) : certificate.expired ? (
                          <StatusBadge value="EXPIRED" />
                        ) : (
                          'n/a'
                        )}
                      </td>
                      <td data-label="Status"><StatusBadge value={certificate.active ? 'ACTIVE' : 'INACTIVE'} /></td>
                      <td data-label="Action" className="actions-cell">
                        {certificate.active && canManage ? (
                          <button
                            type="button"
                            className="danger-link"
                            onClick={() => setConfirm({
                              title: 'Deactivate AS2 certificate',
                              message: `Deactivate ${certificate.alias}. Outbound/inbound flows relying on this certificate will stop trusting it. This requires flow-admin.`,
                              confirmLabel: 'Deactivate',
                              risk: 'high',
                              requireText: 'DEACTIVATE',
                              onConfirm: () => deactivate(certificate.id),
                            })}
                          >
                            Deactivate
                          </button>
                        ) : certificate.active ? (
                          <span className="mono" title="Requires flow-admin">Requires flow-admin</span>
                        ) : (
                          <span className="mono">deactivated</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination-bar">
              <button
                type="button"
                onClick={() => setFilters((current) => ({ ...current, page: Math.max(0, (current.page ?? 0) - 1) }))}
                disabled={(filters.page ?? 0) <= 0}
              >
                Previous
              </button>
              <span>Page {(filters.page ?? 0) + 1}</span>
              <button
                type="button"
                onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 0) + 1 }))}
                disabled={((data?.page ?? 0) + 1) * (data?.pageSize ?? PAGE_SIZE) >= (data?.total ?? 0)}
              >
                Next
              </button>
            </div>
          </>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Register certificate</h2>
          <p>Registers a certificate on the AS2 gateway. Private key material is never accepted here — reference a secret store entry instead.</p>
        </div>
        {!canManage ? (
          <EmptyState label="Registering a certificate requires the flow-admin role." />
        ) : (
        <form className="form-grid" onSubmit={submitForm}>
          <label>
            <span>Alias</span>
            <input value={form.alias} onChange={(event) => setForm((current) => ({ ...current, alias: event.target.value }))} />
          </label>
          <label>
            <span>Certificate type</span>
            <select
              value={form.certificateType}
              onChange={(event) => setForm((current) => ({ ...current, certificateType: event.target.value as As2CertificateType }))}
            >
              {CERTIFICATE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </label>
          <label>
            <span>Partner code</span>
            <input value={form.partnerCode ?? ''} onChange={(event) => setForm((current) => ({ ...current, partnerCode: event.target.value }))} />
          </label>
          <label>
            <span>Private key reference</span>
            <input
              placeholder="vault://secrets/as2/..."
              value={form.privateKeyReference ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, privateKeyReference: event.target.value }))}
            />
          </label>
          <label className="full-width">
            <span>Certificate PEM</span>
            <textarea
              rows={8}
              value={form.certificatePem}
              onChange={(event) => setForm((current) => ({ ...current, certificatePem: event.target.value }))}
            />
          </label>
          {submitError ? <div className="form-error full-width">{submitError}</div> : null}
          <div className="full-width form-actions">
            <button type="submit" disabled={Boolean(registerDisabledReason)} title={registerDisabledReason ?? undefined}>
              {isSubmitting ? 'Registering...' : 'Register certificate'}
            </button>
          </div>
        </form>
        )}
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function shortFingerprint(value?: string): string {
  if (!value) return 'n/a';
  return value.length > 20 ? `${value.slice(0, 20)}...` : value;
}

function formatHealthLabel(value: As2CertificateHealthStatus): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatCertificateError(caught: unknown): string {
  if (caught instanceof ApiClientError && caught.status === 403) {
    return 'Not authorized to manage AS2 certificates. This requires the flow-admin role.';
  }
  return caught instanceof Error ? caught.message : 'Unexpected certificate registration failure.';
}
